import { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_HEADERS, ENDPOINTS } from "../../constants/api";
import { baseQueryWithAuthCheck } from "./helper";
import {
  AlertResponse,
  CampaignData,
  CampaignGraph,
  CandidatePipeline,
  DownloadExcelData,
  FolderData,
  GetPollingResponse,
  InventoryData,
  JobPostData,
  ProfileCountResponse,
  SavedSearch,
  SearchQuery,
  communicationsData,
} from "../../types/api-response";
import {
  GetPollingPayload,
  GetShareFolderRecDetailsPaylod,
  inventoryPayload,
  moveToPhoneScreenPayload,
  renewPayload,
} from "@/types/api-payload";
import { callFromMiddleware, extractDomain } from "@/utils/index";
import { FETCH_DOWNLOADS, FETCH_CAMPAIGN,FETCH_FOLDERS, FETCH_REC_DETAILS,FETCH_COMMUNICATIONS,FETCH_JOBS,FETCH_ALERTS, FETCH_INVENTORY } from "./tags";
import { ManageFolderAPIRecShareRes } from "@/types/index";
import { getUserDetails } from "@/utils/getUserDetails";
const baseQuery = fetchBaseQuery({
  baseUrl: window.location.origin,
  headers: API_HEADERS,
  credentials: "include",
});
const local = window.location.hostname.includes("localhost") ? true : false;
const userDetails=getUserDetails()

export const dashboardApi = createApi({
  reducerPath: "dashboard",
  baseQuery: baseQueryWithAuthCheck(baseQuery),
  tagTypes: [FETCH_DOWNLOADS,FETCH_CAMPAIGN,FETCH_FOLDERS, FETCH_COMMUNICATIONS,FETCH_REC_DETAILS,FETCH_JOBS,FETCH_ALERTS, FETCH_INVENTORY],
  endpoints: (builder) => ({
    getCandidatePipelineCount: builder.query<
      Partial<CandidatePipeline>,
      number
    >({
      query: (args) => ({
        url: callFromMiddleware(
          `${ENDPOINTS.getCandidatePipeline}/${args}`
        ),
        method: "POST",
        headers: API_HEADERS,
        params: {},
        body: {},
      }),
    }),
    getInventory: builder.query<InventoryData, void>({
      query: () => ({
        url: callFromMiddleware(ENDPOINTS.inventory),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
      providesTags: [FETCH_INVENTORY],
    }),
    getInterested: builder.query<any,void>({
      
      query: () => (
        {
          url: local ? `${callFromMiddleware(ENDPOINTS.getInterested)}?func=recommendEnquire&field=params&value=%7B%22product%22:%22ED%22%7D` : `${extractDomain()}${ENDPOINTS.getInterested}?func=recommendEnquire&field=params&value=%7B%22product%22:%22ED%22%7D`,
          method: "GET",
          headers: API_HEADERS,
          params: {},
        }),
    }),
    getLinkedSearchRedirectionUrl: builder.query<any, any>({
      query: (args) => ({
        url:local?callFromMiddleware(ENDPOINTS.getLinkedSearchUrl): extractDomain() + ENDPOINTS.getLinkedSearchUrl,
        method: "GET",
        headers: API_HEADERS,
        params: args,
      }),
    }),
    getInsightFolders: builder.query<Partial<FolderData>, void>({
      query: (args) => ({
        url: callFromMiddleware(`${ENDPOINTS.getInsightFolders}`),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
      providesTags: [FETCH_FOLDERS]
    }),
    getInsightJobs: builder.query<Partial<JobPostData>, void>({
      query: (args) => ({
        url: callFromMiddleware(`${ENDPOINTS.getInsightJobs}`),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
      providesTags: [FETCH_JOBS]
    }),
    getInsightEmailCampaign: builder.query<Partial<CampaignData>, void>({
      query: (args) => ({
        url: callFromMiddleware(`${ENDPOINTS.getInsightEmailCampaign}`),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
      providesTags: [FETCH_CAMPAIGN]
    }),
    getSavedSearch: builder.query<Partial<SavedSearch>, void>({
      query: (args) => ({
        url: callFromMiddleware(`${ENDPOINTS.getSavedSearch}`),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
    }),
    getRecruiters: builder.query<any, any>({
      query: (args) => ({
        url: callFromMiddleware(`${ENDPOINTS.getRecruiters}?folderId=${args}&searchName=`),
        method: "POST",
        headers: API_HEADERS,
        params: {},
        body: {},
      }),
    }),
    shareFolder: builder.mutation<any, any>({
      query: (args) => ({
        url: callFromMiddleware(`${ENDPOINTS.shareFolder}?folderId=${args?.folderId}&share=${args?.shareValue}`),
        method: "POST",
        headers: API_HEADERS,
        params: {},
        body: [args.uid],
      }),
      // invalidatesTags: [FETCH_FOLDERS],
    }),

    
    getCampaignDetails: builder.query<Partial<CampaignGraph>, number>({
      query: (campaignId) => ({
        url: callFromMiddleware(
          `${ENDPOINTS.getCampaignDetails}/details?campaignId=${campaignId}`
        ),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
    }),
    getDownloads: builder.query<Partial<DownloadExcelData>, void>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.getDownloads),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
      providesTags:[FETCH_DOWNLOADS]
    }),
    getRecentSearches: builder.query<Partial<SearchQuery>, any>({
      query: (extraHeaders) => ({
        url: local
          ? callFromMiddleware(ENDPOINTS.getRececntSearches)
          : `${extractDomain()}${ENDPOINTS.getRececntSearches}`,
        method: "GET",
        headers: { ...API_HEADERS, ...extraHeaders },
        params: {},
      }),
    }),

    getCommunicationsData: builder.query<Partial<communicationsData>, void>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.getCommuincations),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
      providesTags: [FETCH_COMMUNICATIONS]
    }),
    pauseCampaign: builder.mutation<any, number | undefined>({
      query: (campaignId) => {
        return ({
        url: callFromMiddleware(
          `${ENDPOINTS.pauseCampaign}/${campaignId}`
        ),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      })},
      // invalidatesTags: [FETCH_CAMPAIGN],
    }),
    moveToState: builder.mutation<any, moveToPhoneScreenPayload>({
      query: (payload) => ({
        url: callFromMiddleware(ENDPOINTS.moveToState),
        method: 'POST',
        headers: API_HEADERS,
        body: payload
      }),
      // invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_COMMUNICATIONS]),
    }),
    deleteJob: builder.mutation({
      query: (jobId) => ({
        url: callFromMiddleware(`${ENDPOINTS.deleteJob}?folderId=${jobId}`),
        method: 'DELETE',
        headers: API_HEADERS,
      }),
      // invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_JOBS]),
    }),
    removeProfile: builder.mutation<any, any>({
      query: (payload) => ({
        url: callFromMiddleware(ENDPOINTS.removeProfile),
        method: 'POST',
        headers: API_HEADERS,
        body: payload
      }),
      // invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_COMMUNICATIONS]),
    }),
    getAlerts: builder.query<Partial<AlertResponse>, void>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.gettAlerts),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
      providesTags: [FETCH_ALERTS]
    }),
    getMailboxValidation: builder.query<any, void>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.mailboxCheck),
        method: "GET",
        headers: API_HEADERS,
        params: {},
      }),
      providesTags: [FETCH_ALERTS]
    }),
    scheduleNow: builder.mutation<any, any>({
      query: (args) => ({
        url: callFromMiddleware(`${ENDPOINTS.scheduleNow}/${args}`),
        method: 'GET',
        headers: API_HEADERS,
      }),
      invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_CAMPAIGN, FETCH_INVENTORY]),
    }),
    renewJob: builder.mutation<any, Partial<renewPayload>>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.renew),
        method: 'PUT',
        headers: API_HEADERS,
        body:[args]
      }),
      // invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_ALERTS]),
    }),
    topUp: builder.mutation<any, any>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.topUp),
        method: 'POST',
        headers: API_HEADERS,
        params:args,
        body: {},
      }),
    }),
    getDownloadProfilesPolling: builder.query<
      any,
      any
    >({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.getDownloadProfilesPolling),
        method: "POST",
        headers: API_HEADERS,
        params: {},
        body: args,
      }),
    }),
 getRevampServices: builder.query<any, any>({
  query: (corpId) => ({
    url: callFromMiddleware(
      `/edge-folder-management/v2/folder/revamp/services?corpId=${corpId}`
    ),
    method: "GET",
    headers: {
      domain: extractDomain(),
      ...API_HEADERS,
    },
  }),
}),

    checkInventory: builder.query({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.checkInventory),
        method: "POST",
        body: args,
        headers: API_HEADERS,
        refetchOnMountOrArgChange: true,
      }),
    }),
    downloadExcelNew: builder.query<any, any>({
      query: (args) => ({
          url: callFromMiddleware(ENDPOINTS.downloadExcelNew),
          method: "POST",
          headers: API_HEADERS,
          body:args
        }),
    }),
    checkEmail: builder.query<any, void>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.checkEmail),
        method: "GET",
        headers: API_HEADERS,
      }),
    }),
    campaignMarkRead: builder.mutation({
      query: (args) => ({
        url: callFromMiddleware(`${ENDPOINTS.campaignMarkRead}?eventId=${args.id}&communicationType=${args.type}`),
        method: "POST",
        body: {},
        headers: API_HEADERS,
      }),
      // invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_COMMUNICATIONS]),
    }),
    downloadsMarkRead: builder.mutation({
      query: (id) => ({
        url: callFromMiddleware(`${ENDPOINTS.downloadsMarkRead}?downloadId=${id}`),
        method: "POST",
        body: {},
        headers: API_HEADERS,
      }),
      // invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_DOWNLOADS]),
    }),
    alertMarkRead: builder.mutation({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.alertMarkRead),
        method: "POST",
        body: args,
        headers: API_HEADERS,
      }),
      // invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_ALERTS])
    }),
    getShareFolderRecDetails: builder.query<
      ManageFolderAPIRecShareRes,
      GetShareFolderRecDetailsPaylod
    >({
      query: (args: any) => ({
        url: `${callFromMiddleware(ENDPOINTS.getManageFolderRec)}/recruiters`,
        method: "POST",
        headers: API_HEADERS,
        params: { folderId: args.folderId, searchName: args.searchName },
        body: {},
      }),
      providesTags: [FETCH_REC_DETAILS],
    }),
    getProfilesCount: builder.query<
    Partial<ProfileCountResponse>,
    { folderId: number }
  >({
    query: (args) => ({
      url: callFromMiddleware(ENDPOINTS.getProfilesCount),
      method: "POST",
      headers: API_HEADERS,
      params: args,
      body: {},
    }),
  }),
  trackHealth : builder.query<any, any>({
    query: (body) => ({
      url: callFromMiddleware(ENDPOINTS.healthTracker),
      method: "POST",
      headers: API_HEADERS,
      body,
    }),
  }),
  checkRevampService : builder.query<any, any>({
    query: (args) => ({
      url: callFromMiddleware(ENDPOINTS.checkRevampService),
      method: "GET",
      headers: API_HEADERS,
      params: args,
    }),
  }),
  }),
});

export const {
  useGetRevampServicesQuery,
  useGetCandidatePipelineCountQuery,
  useGetShareFolderRecDetailsQuery,
  useGetInventoryQuery,
  useGetInsightFoldersQuery,
  useGetInsightJobsQuery,
  useGetInsightEmailCampaignQuery,
  useGetCampaignDetailsQuery,
  useGetSavedSearchQuery,
  useGetRecentSearchesQuery,
  useLazyGetCampaignDetailsQuery,
  useLazyGetInterestedQuery,
  useLazyGetCandidatePipelineCountQuery,
  useGetDownloadsQuery,
  useGetCommunicationsDataQuery,
  usePauseCampaignMutation,
  useMoveToStateMutation,
  useRemoveProfileMutation,
  useLazyGetLinkedSearchRedirectionUrlQuery,
  useDeleteJobMutation,
  useLazyGetRecruitersQuery,
  useShareFolderMutation,
  useGetAlertsQuery,
  useScheduleNowMutation,
  useLazyCheckInventoryQuery,
  useRenewJobMutation,
  useTopUpMutation,
  useGetDownloadProfilesPollingQuery,
  useGetMailboxValidationQuery,
  useCampaignMarkReadMutation,
  useDownloadsMarkReadMutation,
  useAlertMarkReadMutation,
  useLazyCheckEmailQuery,
  useLazyDownloadExcelNewQuery,
  useLazyGetProfilesCountQuery,
  useLazyTrackHealthQuery,
  useLazyCheckRevampServiceQuery,
} = dashboardApi;

export default dashboardApi;
